import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCartItems, updateCartItemQuantity, removeFromCart, updateMemoryHeight, getDisplayUrl } from '../services/memoryService';
import { getDeliveryFees } from '../services/supabase';
import { getUserProfile } from '../services/userProfileService';
import Navbar from '../components/layout/Navbar';
import Button from '../components/common/Button';
import CreditOption from '../components/common/CreditOption';
import { RotateCcw, ShoppingBag, Minus, Plus, Trash2, ArrowRight } from 'lucide-react';
import logger from '../utils/logger';
import './ShoppingCart.css'; // Keep CSS in same folder as component

function ShoppingCart() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [itemHeights, setItemHeights] = useState({});
  const [displayUrls, setDisplayUrls] = useState({});
  const [deliveryFees, setDeliveryFees] = useState([]);
  const [selectedDeliveryFee, setSelectedDeliveryFee] = useState(null);
  const [userCredit, setUserCredit] = useState(0);
  const [useCredit, setUseCredit] = useState(false);

  const formatAud = (amount) => {
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(
        Number(amount || 0)
      );
    } catch (error) {
      logger.warn('Error formatting currency:', error);
      return `AUD ${Number(amount || 0).toFixed(2)}`;
    }
  };

  const loadDisplayUrls = async (cartItems) => {
    const urlPromises = cartItems.map(async (cartItem) => {
      if (cartItem.memories) {
        const memory = cartItem.memories;
        const imageUrl = memory.figurine_url || memory.photo_url;
        if (imageUrl) {
          try {
            const displayUrl = await getDisplayUrl(imageUrl);
            return { id: cartItem.id, displayUrl };
          } catch (error) {
            console.error('Error getting display URL for cart item:', cartItem.id, error);
            return { id: cartItem.id, displayUrl: imageUrl };
          }
        }
      }
      return null;
    });

    const results = await Promise.all(urlPromises);
    const urlMap = {};
    results.forEach(result => {
      if (result) {
        urlMap[result.id] = result.displayUrl;
      }
    });
    setDisplayUrls(urlMap);
  };

  // Fetch cart items and delivery fees from database
  useEffect(() => {
    const fetchData = async () => {
      // Wait for auth to finish initializing before deciding what to do
      if (authLoading) return;

      if (!user) {
        setError('Please log in to view your cart');
        setLoading(false);
        return;
      }

      try {
        // Clear any stale error once we have a user
        setError('');
        setLoading(true);
        
        // Fetch cart items, delivery fees, and user profile in parallel
        const [items, deliveryFeesResult, userProfileResult] = await Promise.all([
          getCartItems(),
          getDeliveryFees(),
          getUserProfile()
        ]);
        
        setCartItems(items);
        setDeliveryFees(deliveryFeesResult.data || []);
        
        // Set user credit if available
        if (userProfileResult.data && userProfileResult.data.credit) {
          setUserCredit(Number(userProfileResult.data.credit));
        }
        
        // Set default delivery fee (first one or free delivery if available)
        if (deliveryFeesResult.data && deliveryFeesResult.data.length > 0) {
          const freeDelivery = deliveryFeesResult.data.find(fee => fee.fee === 0);
          const defaultFee = freeDelivery || deliveryFeesResult.data[0];
          setSelectedDeliveryFee(defaultFee);
        }
        
        await loadDisplayUrls(items);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load cart data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, authLoading]);

  // No background timeouts required; saving on blur only

  const updateQuantity = async (cartItemId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      await updateCartItemQuantity(cartItemId, newQuantity);
      // Update local state
      setCartItems((items) =>
        items.map((item) => (item.id === cartItemId ? { ...item, quantity: newQuantity } : item))
      );
    } catch (err) {
      console.error('Error updating quantity:', err);
      setError('Failed to update quantity. Please try again.');
    }
  };

  const updateHeight = (cartItemId, newHeight, memoryId) => {
    // Allow free typing: store raw value as-is. Persist on blur.
    setItemHeights(prev => ({
      ...prev,
      [cartItemId]: newHeight
    }));
  };

  const getItemHeight = (cartItemId, memoryHeight) => {
    // If user is typing, prefer the raw value (including empty string)
    if (Object.prototype.hasOwnProperty.call(itemHeights, cartItemId)) {
      return itemHeights[cartItemId];
    }
    return memoryHeight ?? 75;
  };

  const handleHeightBlur = async (cartItemId, memoryId) => {
    // Get current height value
    const currentHeight = itemHeights[cartItemId];
    const parsed = parseInt(currentHeight);
    const height = Number.isFinite(parsed) ? Math.max(20, Math.min(80, parsed)) : 75;
    try {
      await updateMemoryHeight(memoryId, height);
      // Replace raw with clamped persisted value
      setItemHeights(prev => ({ ...prev, [cartItemId]: String(height) }));
    } catch (err) {
      console.error('Error updating height on blur:', err);
      setError('Failed to update height. Please try again.');
    }
  };

  const removeItem = async (cartItemId) => {
    try {
      await removeFromCart(cartItemId);
      // Update local state
      setCartItems((items) => items.filter((item) => item.id !== cartItemId));
    } catch (err) {
      console.error('Error removing item:', err);
      setError('Failed to remove item. Please try again.');
    }
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const calculateFinalTotal = () => {
    const subtotal = getTotalPrice();
    const deliveryFee = selectedDeliveryFee?.fee || 0;
    const totalBeforeCredit = subtotal + deliveryFee;
    
    if (useCredit && userCredit > 0) {
      const creditApplied = Math.min(userCredit, totalBeforeCredit);
      return Math.max(0, totalBeforeCredit - creditApplied);
    }
    
    return totalBeforeCredit;
  };

  const getCreditApplied = () => {
    if (useCredit && userCredit > 0) {
      const subtotal = getTotalPrice();
      const deliveryFee = selectedDeliveryFee?.fee || 0;
      const totalBeforeCredit = subtotal + deliveryFee;
      return Math.min(userCredit, totalBeforeCredit);
    }
    return 0;
  };

  const handleCheckout = () => {
    // Navigate to checkout page with cart items, selected delivery fee, and credit info
    navigate('/checkout', {
      state: { 
        cartItems: cartItems,
        selectedDeliveryFee: selectedDeliveryFee,
        creditApplied: getCreditApplied(),
        useCredit: useCredit
      },
    });
  };

  const handleContinueShopping = () => {
    navigate('/home');
  };

  // Show loading state
  if (loading) {
    return (
      <div className="shopping-cart">
        <Navbar />

        <div className="cart-body empty">
          <div className="content">
            <div className="cart-header">
              <h1>Shopping Cart</h1>
            </div>
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading your cart...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="shopping-cart">
        <Navbar />

        <div className="cart-body empty">
          <div className="content">
            <div className="cart-header">
              <h1>Shopping Cart</h1>
            </div>
            <div className="error-container">
              <div className="error-icon">⚠️</div>
              <h2>Error</h2>
              <p>{error}</p>
              <Button
                variant="secondary"
                className="retry-btn"
                onClick={() => window.location.reload()}
                icon={RotateCcw}
              >
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="shopping-cart">
        <Navbar />

        <div className="cart-body empty">
          <div className="content">
            <div className="cart-header">
              <h1>Shopping Cart</h1>
            </div>
            <div className="empty-cart">
              <div className="empty-cart-icon">
                <span className="material-symbols-outlined">shopping_cart</span>
              </div>
              <h2>Your cart is empty</h2>
              <p>Looks like you haven't added any memories to your cart yet.</p>
              <Button
                variant="primary"
                className="continue-shopping-btn"
                onClick={handleContinueShopping}
                icon={ShoppingBag}
              >
                Continue Shopping
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shopping-cart">
      <Navbar />

      <div className="cart-body">
        <div className="content">
          <div className="cart-header">
            <h1>Shopping Cart</h1>
            <p>
              There are{' '}
              <strong>
                {cartItems.length} item{cartItems.length !== 1 ? 's' : ''}
              </strong>{' '}
              in your cart
            </p>
            {/* <p className="cart-instruction">
              Review your quantities and proceed to Checkout when you are ready
            </p> */}
            <p className="cart-instruction">
              Your tiniego will be <strong>printed in white with a base</strong>. You can pick the height you'd like, up to 80 mm total (that includes the 3 mm base).
            </p>
          </div>

          <div className="cart-container">
            <div className="cart-items">
              {cartItems.map((cartItem) => (
                <div key={cartItem.id} className="cart-item">
                  <div className="item-image">
                    <img
                      src={
                        displayUrls[cartItem.id] ||
                        cartItem.memories?.figurine_url ||
                        cartItem.memories?.photo_url ||
                        '/placeholder-image.png'
                      }
                      alt={cartItem.memories?.title || 'Memory'}
                      loading="lazy"
                    />
                  </div>
                  <div className="item-details">
                    <div className="item-title-price">
                      <h3>{cartItem.memories?.title || 'Memory'}</h3>
                      <div className="item-price">
                        {formatAud(cartItem.price * cartItem.quantity)}
                      </div>
                    </div>
                  </div>
                  
                  <div className="item-separator"></div>
                  
                  <div className="height-section">
                    <label className="height-label" htmlFor={`height-${cartItem.id}`}>
                      Height (max 80mm)
                    </label>
                    <div className="height-input-container">
                      <input
                        id={`height-${cartItem.id}`}
                        type="number"
                        className="height-input"
                        min="20"
                        max="80"
                        value={getItemHeight(cartItem.id, cartItem.memories?.height)}
                        onChange={(e) => updateHeight(cartItem.id, e.target.value, cartItem.memories?.id)}
                        onBlur={() => handleHeightBlur(cartItem.id, cartItem.memories?.id)}
                        placeholder="75"
                      />
                      <span className="height-unit">mm</span>
                    </div>
                  </div>
                  
                  <div className="item-separator"></div>
                  
                  <div className="item-controls">
                    <div className="item-quantity">
                      <Button
                        variant="outline"
                        size="sm"
                        className="quantity-btn"
                        onClick={() => updateQuantity(cartItem.id, cartItem.quantity - 1)}
                        icon={Minus}
                      />
                      <span className="quantity"> x {cartItem.quantity}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="quantity-btn"
                        onClick={() => updateQuantity(cartItem.id, cartItem.quantity + 1)}
                        icon={Plus}
                      />
                    </div>
                    <Button
                      variant="danger"
                      className="remove-btn"
                      onClick={() => removeItem(cartItem.id)}
                      icon={Trash2}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="cart-summary-container">
              {/* Show credit option if user has available credit */}
              {userCredit > 0 && (
                <CreditOption
                  creditAmount={userCredit}
                  isSelected={useCredit}
                  onToggle={() => setUseCredit(!useCredit)}
                  formatAud={formatAud}
                />
              )}

              <div className="cart-summary">
              <h3>Order Summary</h3>
              {cartItems.map((cartItem) => (
                <div key={cartItem.id} className="summary-row item-row">
                  <span>
                    {cartItem.memories?.title || 'Memory'} (x{cartItem.quantity})
                  </span>
                  <span>{formatAud(cartItem.price * cartItem.quantity)}</span>
                </div>
              ))}
              <div className="summary-row">
                <span>Subtotal:</span>
                <span>{formatAud(getTotalPrice())}</span>
              </div>
              
              {/* Delivery Options */}
              <div className="delivery-section">
                <h4>Delivery Options</h4>
                {deliveryFees.map((fee) => (
                  <div key={fee.id} className="delivery-option">
                    <div className="delivery-radio-container">
                      <input
                        type="radio"
                        name="delivery"
                        value={fee.id}
                        checked={selectedDeliveryFee?.id === fee.id}
                        onChange={() => setSelectedDeliveryFee(fee)}
                      />
                    </div>
                    <div className="delivery-info-container">
                      <span className="delivery-name">{fee.name}</span>
                      {fee.description && (
                        <p className="delivery-description">{fee.description}</p>
                      )}
                    </div>
                    <div className="delivery-price-container">
                      <span className="delivery-price">{formatAud(fee.fee)}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Show credit deduction if credit is being used */}
              {useCredit && userCredit > 0 && (
                <div className="summary-row credit-row">
                  <span>Credit Applied:</span>
                  <span className="credit-deduction">-{formatAud(getCreditApplied())}</span>
                </div>
              )}
              
              <div className="summary-row total">
                <span>Total:</span>
                <span>{formatAud(calculateFinalTotal())}</span>
              </div>
              <Button variant="primary" className="checkout-btn" onClick={handleCheckout} icon={ArrowRight}>
                Proceed to Checkout
              </Button>
              <Button
                variant="secondary"
                className="continue-shopping-btn"
                onClick={handleContinueShopping}
                icon={ShoppingBag}
              >
                Continue Shopping
              </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ShoppingCart;
